import { useState } from 'react';
import { Loader2, Percent, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Collapsible({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-medium hover:underline w-full text-left"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
        {badge}
      </button>
      {open && <div className="mt-2 ml-6">{children}</div>}
    </div>
  );
}

function Kv({ label, value, mono = false }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground min-w-[180px]">{label}:</span>
      <span className={mono ? 'font-mono' : ''}>{value ?? 'n/a'}</span>
    </div>
  );
}

function fmt(v) {
  if (v == null) return 'n/a';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(v) {
  if (v == null) return 'n/a';
  return (v * 100).toFixed(2) + '%';
}

const htBadgeVariant = {
  likely_ht: 'default',
  likely_ttc: 'secondary',
  indeterminate: 'destructive',
};

export function CommissionClassificationPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [entityId, setEntityId] = useState('');
  const [month, setMonth] = useState('');

  const run = async () => {
    if (!entityId || !month) {
      toast.error('Both entity_id and month are required.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.commissionClassification({
        entity_id: entityId,
        month,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Percent className="h-5 w-5 text-violet-600" />
          Management Fee Classification
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only diagnostic classifying management-statement lines for management
          fee actuals. Shows per-booking fee, revenue anchors, and HT/TTC
          plausibility so the operator can confirm the right line and basis before wiring.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Entity ID</label>
            <input
              type="text"
              className="border rounded px-2 py-1 text-sm w-32"
              placeholder="e.g. bi_uk"
              value={entityId}
              onChange={e => setEntityId(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Month</label>
            <input
              type="text"
              className="border rounded px-2 py-1 text-sm w-28"
              placeholder="YYYY-MM"
              value={month}
              onChange={e => setMonth(e.target.value)}
            />
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Classify
          </Button>
        </div>

        {r && !r.statements_present && (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded p-3">
            No management statements found for {r.entity_name} ({r.entity_id}) in {r.month}.
          </div>
        )}

        {r && r.statements_present && (
          <div className="space-y-4">
            {/* Header */}
            <div className="bg-muted/50 rounded p-3 text-sm space-y-1">
              <Kv label="Entity" value={`${r.entity_name} (${r.entity_id})`} />
              <Kv label="Month" value={r.month} />
              <Kv label="Statements" value={r.statement_count} mono />
            </div>

            {/* Aggregate summary */}
            <Collapsible
              title="Aggregate Summary"
              badge={
                <Badge
                  variant={htBadgeVariant[r.aggregate?.plausibility?.ht_status] || 'secondary'}
                  className="text-xs ml-2"
                >
                  {r.aggregate?.plausibility?.ht_status || 'unknown'}
                </Badge>
              }
              defaultOpen
            >
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <Kv label="Total fee" value={fmt(r.aggregate?.total_commission)} mono />
                  <Kv label="Total net revenue" value={fmt(r.aggregate?.total_net_revenue)} mono />
                  <Kv label="Total gross revenue" value={fmt(r.aggregate?.total_gross_revenue)} mono />
                  <Kv label="Ratio vs net" value={pct(r.aggregate?.ratio_vs_net)} mono />
                  <Kv label="Ratio vs gross" value={pct(r.aggregate?.ratio_vs_gross)} mono />
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5 mt-2">
                  {r.aggregate?.plausibility?.findings?.map((f, i) => (
                    <div key={i}>{f}</div>
                  ))}
                </div>
              </div>
            </Collapsible>

            {/* Per-property summaries */}
            <Collapsible
              title="Per-Property Revenue Anchors"
              badge={
                <Badge variant="outline" className="text-xs ml-2">
                  {r.property_summaries?.length || 0} properties
                </Badge>
              }
              defaultOpen
            >
              {r.property_summaries?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-2">Property</th>
                        <th className="py-1 pr-2 text-right">Fee</th>
                        <th className="py-1 pr-2 text-right">Net Rev</th>
                        <th className="py-1 pr-2 text-right">Gross Rev</th>
                        <th className="py-1 pr-2 text-right">Bookings</th>
                        <th className="py-1 pr-2 text-right">Ratio/Net</th>
                        <th className="py-1 pr-2 text-right">Ratio/Gross</th>
                        <th className="py-1 pr-2">HT Status</th>
                        <th className="py-1 pr-2 text-right">HT Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.property_summaries.map((p, i) => (
                        <tr key={i} className="border-b last:border-b-0">
                          <td className="py-1 pr-2 font-mono">{p.property_ref}</td>
                          <td className="py-1 pr-2 text-right font-mono">{fmt(p.commission_total)}</td>
                          <td className="py-1 pr-2 text-right font-mono">{fmt(p.net_revenue)}</td>
                          <td className="py-1 pr-2 text-right font-mono">{fmt(p.gross_revenue)}</td>
                          <td className="py-1 pr-2 text-right">{p.smoobu_booking_count}</td>
                          <td className="py-1 pr-2 text-right font-mono">{pct(p.ratio_vs_net)}</td>
                          <td className="py-1 pr-2 text-right font-mono">{pct(p.ratio_vs_gross)}</td>
                          <td className="py-1 pr-2">
                            <Badge
                              variant={htBadgeVariant[p.plausibility?.ht_status] || 'secondary'}
                              className="text-xs"
                            >
                              {p.plausibility?.ht_status || '?'}
                            </Badge>
                          </td>
                          <td className="py-1 pr-2 text-right font-mono">{p.ht_amount != null ? fmt(p.ht_amount) : 'n/a'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No property data.</p>
              )}
            </Collapsible>

            {/* Per-statement detail */}
            {r.statements?.map((stmt, si) => (
              <Collapsible
                key={si}
                title={`Statement: ${stmt.property_name_raw || stmt.statement_id}`}
                badge={
                  <Badge variant="outline" className="text-xs ml-2">
                    {stmt.booking_count} bookings, fee {fmt(stmt.commission_total)}
                  </Badge>
                }
              >
                <div className="space-y-3">
                  {/* Statement metadata */}
                  <div className="text-sm space-y-1">
                    <Kv label="Statement ID" value={stmt.statement_id} mono />
                    <Kv label="Property ref" value={stmt.property_ref} mono />
                    <Kv label="Agency" value={stmt.agency} />
                  </div>

                  {/* Totals */}
                  <div className="text-sm space-y-1">
                    <Kv label="Income gross" value={fmt(stmt.totals?.income_gross)} mono />
                    <Kv label="Total fee" value={fmt(stmt.totals?.total_commission)} mono />
                    <Kv label="Income net" value={fmt(stmt.totals?.income_net)} mono />
                    <Kv label="Total expenses" value={fmt(stmt.totals?.total_expenses)} mono />
                    <Kv label="Net profit" value={fmt(stmt.totals?.net_profit)} mono />
                  </div>

                  {/* Commission cross-check */}
                  <div className="text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Fee cross-check:</span>
                      <Badge
                        variant={stmt.commission_cross_check?.match ? 'default' : 'destructive'}
                        className="text-xs"
                      >
                        {stmt.commission_cross_check?.match ? 'OK' : 'MISMATCH'}
                      </Badge>
                      <span className="font-mono text-xs">
                        per-booking sum {fmt(stmt.commission_cross_check?.per_booking_sum)} vs
                        totals {fmt(stmt.commission_cross_check?.totals_total_commission)}
                      </span>
                    </div>
                  </div>

                  {/* Bookings */}
                  {stmt.bookings?.length > 0 && (
                    <Collapsible title="Per-booking fee" defaultOpen={stmt.bookings.length <= 10}>
                      <div className="overflow-x-auto">
                        <table className="text-xs w-full">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="py-1 pr-2">Ref</th>
                              <th className="py-1 pr-2">Guest</th>
                              <th className="py-1 pr-2">Channel</th>
                              <th className="py-1 pr-2">Arrival</th>
                              <th className="py-1 pr-2 text-right">Gross</th>
                              <th className="py-1 pr-2 text-right">Fee</th>
                              <th className="py-1 pr-2 text-right">Charges</th>
                              <th className="py-1 pr-2 text-right">Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stmt.bookings.map((bk, bi) => (
                              <tr key={bi} className="border-b last:border-b-0">
                                <td className="py-1 pr-2 font-mono">{bk.booking_ref}</td>
                                <td className="py-1 pr-2 max-w-[120px] truncate">{bk.guest_name}</td>
                                <td className="py-1 pr-2">{bk.channel}</td>
                                <td className="py-1 pr-2">{bk.arrival}</td>
                                <td className="py-1 pr-2 text-right font-mono">{fmt(bk.gross)}</td>
                                <td className="py-1 pr-2 text-right font-mono">{fmt(bk.commission)}</td>
                                <td className="py-1 pr-2 text-right font-mono">{fmt(bk.payment_charges)}</td>
                                <td className="py-1 pr-2 text-right font-mono">{fmt(bk.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Collapsible>
                  )}

                  {/* Expenses (classified) */}
                  {stmt.expenses?.length > 0 && (
                    <Collapsible title="Expenses (classified)">
                      <div className="overflow-x-auto">
                        <table className="text-xs w-full">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="py-1 pr-2">Date</th>
                              <th className="py-1 pr-2">Supplier</th>
                              <th className="py-1 pr-2">Description</th>
                              <th className="py-1 pr-2">Account</th>
                              <th className="py-1 pr-2 text-right">Amount</th>
                              <th className="py-1 pr-2">Tag</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stmt.expenses.map((exp, ei) => (
                              <tr key={ei} className="border-b last:border-b-0">
                                <td className="py-1 pr-2">{exp.date || ''}</td>
                                <td className="py-1 pr-2 max-w-[120px] truncate">{exp.supplier}</td>
                                <td className="py-1 pr-2 max-w-[200px] truncate">{exp.description}</td>
                                <td className="py-1 pr-2">{exp.account}</td>
                                <td className="py-1 pr-2 text-right font-mono">{fmt(exp.amount)}</td>
                                <td className="py-1 pr-2">
                                  <Badge
                                    variant={exp.classification === 'cleaning' ? 'default' : 'outline'}
                                    className="text-xs"
                                  >
                                    {exp.classification}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Collapsible>
                  )}
                </div>
              </Collapsible>
            ))}

            {/* Note */}
            <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
              {r.note}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
